import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getLightingDiagram, saveLightingDiagram } from '../../services/storyToolsService';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Slider,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Chip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Fab
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LightbulbCircleIcon from '@mui/icons-material/LightbulbCircle';
import HighlightIcon from '@mui/icons-material/Highlight';
import FlashlightOnIcon from '@mui/icons-material/FlashlightOn';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import PersonIcon from '@mui/icons-material/Person';
import BlurLinearIcon from '@mui/icons-material/BlurLinear';
import FlagIcon from '@mui/icons-material/Flag';
import CropLandscapeIcon from '@mui/icons-material/CropLandscape';
import PrintIcon from '@mui/icons-material/Print';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import WindowIcon from '@mui/icons-material/Window';
import DoorFrontIcon from '@mui/icons-material/DoorFront';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { GlobalStyles } from '@mui/material';

// CSS keyframes for animations
const pulseKeyframes = `
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: translateX(-50%) scale(1); }
    50% { opacity: 0.6; transform: translateX(-50%) scale(0.9); }
  }
`;

const clamp = (value, min = 0, max = 100) => Math.min(max, Math.max(min, value));

const FEET_INCH_REGEX = /^\s*(\d+)(?:\s*(?:ft|'|feet))?(?:\s*(\d+)(?:\s*(?:in|"|inches))?)?\s*$/i;

const parseFeetInches = (input, fallback = 0) => {
  if (typeof input === 'number') return input;
  if (typeof input !== 'string') return fallback;
  const trimmed = input.trim();
  if (trimmed === '') return fallback;

  const match = trimmed.match(FEET_INCH_REGEX);
  if (!match) {
    const numeric = Number.parseFloat(trimmed);
    return Number.isFinite(numeric) ? numeric : fallback;
  }

  const feet = Number.parseInt(match[1] ?? '0', 10);
  const inches = Number.parseInt(match[2] ?? '0', 10);
  if (!Number.isFinite(feet) || !Number.isFinite(inches)) return fallback;
  return feet + inches / 12;
};

const formatFeetInches = (value) => {
  if (!Number.isFinite(value)) return '';
  const totalInches = Math.round(value * 12);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  if (inches === 0) return `${feet} ft`;
  if (feet === 0) return `${inches} in`;
  return `${feet} ft ${inches} in`;
};

const formatDecimalValue = (value, fractionDigits = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  const rounded = Number(numeric.toFixed(fractionDigits));
  return rounded.toString();
};

const formatStageDimensionInputValue = (value, unit) => {
  if (!Number.isFinite(value)) return '';
  return unit === 'ft' ? formatFeetInches(value) : formatDecimalValue(value, 2);
};

const parseStageDimensionInputValue = (input, unit, fallback) => {
  if (unit === 'ft') {
    return parseFeetInches(input, fallback);
  }

  if (typeof input === 'number' && Number.isFinite(input)) {
    return input;
  }

  const numeric = Number.parseFloat(typeof input === 'string' ? input : `${input ?? ''}`);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const formatDimensionLabel = (value, unit) => {
  if (!Number.isFinite(value)) return '';
  if (unit === 'ft') {
    return formatFeetInches(value);
  }
  return `${formatDecimalValue(value, 2)} ${unit}`;
};

const formatStructureInputValue = (value, unit) => {
  if (!Number.isFinite(value)) return '';
  return formatDecimalValue(value, 2);
};

const parseStructureInputValue = (value, unit, fallback) => {
  return parseStageDimensionInputValue(value, unit, fallback);
};

const generateFixtureId = () => {
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
    return crypto.randomUUID();
  }
  return `fixture-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
};

const FIXTURE_DEFINITIONS = {
  'Key Light': {
    icon: HighlightIcon,
    swatch: '#fbbf24',
    accent: '#d97706',
    shape: 'round',
    beamStyle: 'hard',
    description: 'Primary light source'
  },
  'Fill Light': {
    icon: LightbulbCircleIcon,
    swatch: '#93c5fd',
    accent: '#3b82f6',
    shape: 'round',
    beamStyle: 'soft',
    description: 'Softens shadows'
  },
  'Back Light': {
    icon: FlashlightOnIcon,
    swatch: '#c4b5fd',
    accent: '#7c3aed',
    shape: 'round',
    beamStyle: 'rim',
    description: 'Separates subject from background'
  },
  'Practical': {
    icon: WbIncandescentIcon,
    swatch: '#fdba74',
    accent: '#f97316',
    shape: 'round',
    beamStyle: 'warm',
    description: 'Visible light source in scene'
  },
  'Spot': {
    icon: WbSunnyIcon,
    swatch: '#fca5a5',
    accent: '#dc2626',
    shape: 'round',
    beamStyle: 'spot',
    description: 'Focused beam'
  },
  'Ambient': {
    icon: LightbulbIcon,
    swatch: '#86efac',
    accent: '#16a34a',
    shape: 'round',
    beamStyle: 'ambient',
    description: 'General scene illumination'
  },
  'Diffusion Panel': {
    icon: BlurLinearIcon,
    swatch: '#bae6fd',
    accent: '#0ea5e9',
    shape: 'panel',
    beamStyle: 'diffuse',
    description: 'Softens and spreads light'
  },
  'Bounce Board': {
    icon: CropLandscapeIcon,
    swatch: '#fef08a',
    accent: '#eab308',
    shape: 'panel',
    beamStyle: 'bounce',
    description: 'Reflects light'
  },
  'Flag / Cutter': {
    icon: FlagIcon,
    swatch: '#1e293b',
    accent: '#0f172a',
    iconColor: '#f8fafc',
    shape: 'panel',
    description: 'Blocks or shapes light'
  },
  'Wall': {
    icon: ViewWeekIcon,
    swatch: '#e2e8f0',
    accent: '#475569',
    iconColor: '#1f2937',
    shape: 'structure',
    description: 'Structural wall segment'
  },
  'Window': {
    icon: WindowIcon,
    swatch: '#dbeafe',
    accent: '#1d4ed8',
    iconColor: '#1e3a8a',
    shape: 'structure',
    description: 'Window with panes'
  },
  'Door': {
    icon: DoorFrontIcon,
    swatch: '#fee2e2',
    accent: '#ef4444',
    iconColor: '#7f1d1d',
    shape: 'structure',
    description: 'Doorway'
  },
  'Camera': {
    icon: VideocamIcon,
    swatch: '#1e293b',
    accent: '#0f172a',
    iconColor: '#93c5fd',
    shape: 'camera',
    description: 'Camera position'
  },
  'Talent Marker': {
    icon: PersonIcon,
    swatch: '#fce7f3',
    accent: '#f472b6',
    iconColor: '#be185d',
    shape: 'round',
    description: 'Talent position'
  },
  'Custom': {
    icon: LightbulbIcon,
    swatch: '#e5e7eb',
    accent: '#9ca3af',
    shape: 'round',
    beamStyle: 'custom',
    description: 'Custom fixture'
  }
};

const createFixture = (overrides = {}) => {
  const type = overrides.type ?? 'Custom';
  const definition = FIXTURE_DEFINITIONS[type] ?? FIXTURE_DEFINITIONS.Custom;
  const accentHeight = definition?.shape === 'camera' ? 5 : 12;
  const defaultLength = definition?.shape === 'structure' ? 12 : 6;

  const fixture = {
    id: overrides.id ?? generateFixtureId(),
    type,
    name: overrides.name ?? 'New Fixture',
    color: overrides.color ?? definition?.swatch ?? '#ffffff',
    x: clamp(overrides.x ?? 50, 0, 100),
    y: clamp(overrides.y ?? 50, 0, 100),
    height: STRUCTURE_TYPES.includes(type) ? 1 : overrides.height ?? accentHeight,
    length: overrides.length ?? defaultLength,
    beamAngle: overrides.beamAngle ?? 45,
    rotation: overrides.rotation ?? 0,
    notes: overrides.notes ?? ''
  };


  fixture.shotLabel = overrides.shotLabel ?? (type === 'Camera' ? 'Shot A' : '');
  fixture.lens = overrides.lens ?? (type === 'Camera' ? '35mm Prime' : '');
  fixture.focalLength = overrides.focalLength ?? (type === 'Camera' ? 35 : null);
  fixture.cameraNotes = overrides.cameraNotes ?? '';
  fixture.talentName = overrides.talentName ?? (type === 'Talent Marker' ? 'Talent' : '');
  fixture.talentHeight = overrides.talentHeight ?? (type === 'Talent Marker' ? 5.8 : null);
  fixture.movementNotes = overrides.movementNotes ?? '';

  return fixture;
};

const FIXTURE_TYPES = [
  'Key Light',
  'Fill Light',
  'Back Light',
  'Practical',
  'Spot',
  'Ambient',
  'Diffusion Panel',
  'Bounce Board',
  'Flag / Cutter',
  'Wall',
  'Window',
  'Door',
  'Camera',
  'Talent Marker',
  'Custom'
];

const STRUCTURE_TYPES = ['Wall', 'Window', 'Door'];

const MIN_STRUCTURE_LENGTH_BY_UNIT = {
  ft: 1 / 48, // quarter inch minimum
  m: 0.01
};

const getStructureMinLength = (unit) => {
  if (unit === 'm') {
    return MIN_STRUCTURE_LENGTH_BY_UNIT.m;
  }
  return MIN_STRUCTURE_LENGTH_BY_UNIT.ft;
};

const snapStructureAxisToNeighbors = (value, axis, fixtureId, fixtures, stageDimensions, threshold = 2) => {
  if (!Number.isFinite(value)) return value;
  const axisKey = axis === 'x' ? 'x' : 'y';
  let snappedValue = value;
  let bestDelta = threshold;

  for (const neighbor of fixtures) {
    if (neighbor.id === fixtureId || !STRUCTURE_TYPES.includes(neighbor.type)) {
      continue;
    }

    const candidates = [];
    const neighborCenter = neighbor[axisKey];
    if (Number.isFinite(neighborCenter)) {
      candidates.push(neighborCenter);
    }

    if (STRUCTURE_TYPES.includes(neighbor.type)) {
      const neighborCorners = getStructureCornersPercent(neighbor, stageDimensions, neighbor.x, neighbor.y);
      neighborCorners.forEach((corner) => {
        const cornerValue = corner[axisKey];
        if (Number.isFinite(cornerValue)) {
          candidates.push(cornerValue);
        }
      });
    }

    for (const candidate of candidates) {
      const delta = Math.abs(value - candidate);
      if (delta <= bestDelta) {
        bestDelta = delta;
        snappedValue = candidate;
      }
    }
  }

  return snappedValue;
};

const getStructureSizePercent = (fixture, stageDimensions) => {
  const stageWidth = stageDimensions?.width ?? DEFAULT_STAGE_DIMENSIONS.width;
  const stageHeight = stageDimensions?.height ?? DEFAULT_STAGE_DIMENSIONS.height;

  const length = Number.isFinite(fixture.length) ? fixture.length : stageWidth / 6;
  const height = Number.isFinite(fixture.height) ? fixture.height : stageHeight / 10;

  return {
    width: clamp((length / stageWidth) * 100, 0.5, 100),
    height: clamp((height / stageHeight) * 100, 0.5, 100)
  };
};

const getStructureCornersPercent = (fixture, stageDimensions, centerX, centerY) => {
  if (!STRUCTURE_TYPES.includes(fixture.type)) {
    return [];
  }

  const { width, height } = getStructureSizePercent(fixture, stageDimensions);
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  const localCorners = [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight }
  ];

  const rotation = ((fixture.rotation ?? 0) % 360 + 360) % 360;
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return localCorners.map(({ x, y }) => ({
    x: centerX + x * cos - y * sin,
    y: centerY + x * sin + y * cos
  }));
};

const snapStructurePosition = (centerX, centerY, fixture, fixtures, stageDimensions, threshold = 1.5) => {
  if (!STRUCTURE_TYPES.includes(fixture.type)) {
    return { x: centerX, y: centerY };
  }

  const myCorners = getStructureCornersPercent(fixture, stageDimensions, centerX, centerY);
  if (myCorners.length === 0) {
    return { x: centerX, y: centerY };
  }

  let bestX = centerX;
  let bestY = centerY;
  let bestDistance = threshold;
  let bestMatch = null;

  for (const neighbor of fixtures) {
    if (neighbor.id === fixture.id || !STRUCTURE_TYPES.includes(neighbor.type)) {
      continue;
    }

    const neighborCorners = getStructureCornersPercent(neighbor, stageDimensions, neighbor.x, neighbor.y);
    for (const myCorner of myCorners) {
      for (const neighborCorner of neighborCorners) {
        const dx = neighborCorner.x - myCorner.x;
        const dy = neighborCorner.y - myCorner.y;
        const distance = Math.hypot(dx, dy);
        if (distance <= bestDistance) {
          bestDistance = distance;
          bestX = clamp(centerX + dx, 1, 99);
          bestY = clamp(centerY + dy, 1, 99);
          bestMatch = {
            dx,
            dy,
            neighbor,
            neighborCorner,
            myCorner
          };
        }
      }
    }
  }

  if (bestMatch) {
    const mySize = getStructureSizePercent(fixture, stageDimensions);
    const neighborSize = getStructureSizePercent(bestMatch.neighbor, stageDimensions);
    const myThickness = mySize.height || 0;
    const neighborThickness = neighborSize.height || 0;

    const neighborRotation = ((bestMatch.neighbor.rotation ?? 0) % 360 + 360) % 360;
    const neighborRadians = (neighborRotation * Math.PI) / 180;
    const neighborNormal = {
      x: -Math.sin(neighborRadians),
      y: Math.cos(neighborRadians)
    };

    const cornerVector = {
      x: bestMatch.neighborCorner.x - bestMatch.neighbor.x,
      y: bestMatch.neighborCorner.y - bestMatch.neighbor.y
    };
    const cornerProj = cornerVector.x * neighborNormal.x + cornerVector.y * neighborNormal.y;
    const sideSign = cornerProj >= 0 ? 1 : -1;

    const deltaX = bestX - bestMatch.neighbor.x;
    const deltaY = bestY - bestMatch.neighbor.y;
    let proj = deltaX * neighborNormal.x + deltaY * neighborNormal.y;
    if (proj === 0) {
      proj = 0.0001 * sideSign;
    }

    const requiredDistance = (neighborThickness + myThickness) / 2;
    const targetProj = sideSign * requiredDistance;
    const offset = targetProj - proj;

    if (Math.abs(offset) > 0) {
      bestX = clamp(bestX + neighborNormal.x * offset, 1, 99);
      bestY = clamp(bestY + neighborNormal.y * offset, 1, 99);
    }
  }

  return { x: bestX, y: bestY };
};

const PRESET_BLUEPRINTS = [
  {
    key: 'three-point',
    label: 'Three-Point Cinematic',
    icon: LightbulbIcon,
    accent: '#f97316',
    tags: ['Narrative', 'Balanced', 'Beginner Friendly'],
    keyRatio: '3:1',
    cameraSetup: 'Single cam · 35mm',
    recommendedUse: 'General purpose interviews and dialogue coverage.',
    description: 'Classic Hollywood setup: key, fill, and hair light for dimensional depth.',
    fixtures: [
      {
        name: 'Camera A',
        type: 'Camera',
        x: 50,
        y: 15,
        height: 5,
        rotation: 180,
        shotLabel: 'Shot A',
        lens: '35mm Prime',
        focalLength: 35
      },
      {
        name: 'Back Light',
        type: 'Back Light',
        intensity: 65,
        x: 55,
        y: 20,
        rotation: 180,
        height: 16,
        notes: 'Hair light for separation from background.'
      },
      {
        name: 'Talent Marker',
        type: 'Talent Marker',
        intensity: 0,
        x: 50,
        y: 50,
        rotation: 0,
        talentName: 'Talent'
      },
      {
        name: 'Key Light',
        type: 'Key Light',
        intensity: 82,
        x: 32,
        y: 70,
        rotation: -30,
        height: 11,
        notes: 'Key light positioned for dramatic effect.'
      },
      {
        name: 'Fill Light',
        type: 'Fill Light',
        intensity: 48,
        x: 68,
        y: 70,
        rotation: 30,
        height: 9,
        notes: 'Fill light for shadow detail.'
      }
    ]
  },
  {
    key: 'rembrandt',
    label: 'Rembrandt Lighting',
    icon: LightbulbIcon,
    accent: '#ef4444',
    tags: ['Portrait', 'Moody'],
    keyRatio: '5:1',
    cameraSetup: 'Single cam · 85mm',
    recommendedUse: 'Stylized portraits with dramatic contrast.',
    description: 'Dramatic portrait lighting with signature triangle under the eye.',
    fixtures: [
      {
        name: 'Key Light',
        type: 'Key Light',
        intensity: 85,
        x: 28,
        y: 48,
        rotation: -45,
        height: 14,
        notes: 'Key at 45° angle, elevated for nose shadow.'
      },
      {
        name: 'Fill Light',
        type: 'Fill Light',
        intensity: 35,
        x: 72,
        y: 62,
        rotation: 35,
        height: 8,
        notes: 'Subtle fill to maintain shadow depth.'
      },
      {
        name: 'Back Light',
        type: 'Back Light',
        intensity: 68,
        x: 50,
        y: 22,
        rotation: 180,
        height: 18,
        notes: 'Rim light for subject separation.'
      },
      {
        name: 'Camera A',
        type: 'Camera',
        x: 50,
        y: 88,
        height: 5,
        rotation: 0,
        shotLabel: 'Portrait',
        lens: '85mm Prime',
        focalLength: 85
      }
    ]
  },
  {
    key: 'butterfly',
    label: 'Butterfly (Paramount)',
    icon: LightbulbIcon,
    accent: '#f59e0b',
    tags: ['Beauty', 'Classic Hollywood'],
    keyRatio: '2:1',
    cameraSetup: 'Single cam · 85mm',
    recommendedUse: 'Beauty, glamour, and portrait close-ups.',
    description: 'Glamour lighting with centered key creating butterfly shadow under nose.',
    fixtures: [
      {
        name: 'Key Light',
        type: 'Key Light',
        intensity: 88,
        x: 50,
        y: 42,
        rotation: 0,
        height: 16,
        notes: 'Centered overhead key for butterfly shadow.'
      },
      {
        name: 'Fill Light',
        type: 'Fill Light',
        intensity: 42,
        x: 50,
        y: 72,
        rotation: 0,
        height: 6,
        notes: 'Low fill to soften under-eye shadows.'
      },
      {
        name: 'Back Light',
        type: 'Back Light',
        intensity: 65,
        x: 50,
        y: 18,
        rotation: 180,
        height: 20,
        notes: 'Hair light for glamour separation.'
      },
      {
        name: 'Camera A',
        type: 'Camera',
        x: 50,
        y: 90,
        height: 5,
        rotation: 0,
        shotLabel: 'Glamour',
        lens: '85mm Prime',
        focalLength: 85
      }
    ]
  },
  {
    key: 'split',
    label: 'Split Lighting',
    icon: LightbulbIcon,
    accent: '#6d28d9',
    tags: ['Portrait', 'Cinematic'],
    keyRatio: '8:1',
    cameraSetup: 'Single cam · 50mm',
    recommendedUse: 'High contrast character reveals and dramatic scenes.',
    description: 'Dramatic 90° lighting dividing face into light and shadow halves.',
    fixtures: [
      {
        name: 'Key Light',
        type: 'Key Light',
        intensity: 92,
        x: 18,
        y: 58,
        rotation: -90,
        height: 12,
        notes: '90° side key for dramatic split.'
      },
      {
        name: 'Back Light',
        type: 'Back Light',
        intensity: 58,
        x: 52,
        y: 24,
        rotation: 180,
        height: 16,
        notes: 'Edge light on shadow side.'
      },
      {
        name: 'Camera A',
        type: 'Camera',
        x: 50,
        y: 86,
        height: 5,
        rotation: 0,
        shotLabel: 'Dramatic',
        lens: '50mm Prime',
        focalLength: 50
      }
    ]
  },
  {
    key: 'loop',
    label: 'Loop Lighting',
    icon: LightbulbIcon,
    accent: '#10b981',
    tags: ['Portrait', 'Natural'],
    keyRatio: '4:1',
    cameraSetup: 'Single cam · 85mm',
    recommendedUse: 'Corporate headshots and lifestyle portraits.',
    description: 'Versatile portrait setup with small nose shadow looping toward cheek.',
    fixtures: [
      {
        name: 'Key Light',
        type: 'Key Light',
        intensity: 80,
        x: 32,
        y: 50,
        rotation: -30,
        height: 13,
        notes: 'Key at 30-45° for loop shadow.'
      },
      {
        name: 'Fill Light',
        type: 'Fill Light',
        intensity: 45,
        x: 68,
        y: 60,
        rotation: 25,
        height: 9,
        notes: 'Balanced fill for natural look.'
      },
      {
        name: 'Back Light',
        type: 'Back Light',
        intensity: 62,
        x: 50,
        y: 26,
        rotation: 180,
        height: 17,
        notes: 'Hair light for dimension.'
      },
      {
        name: 'Camera A',
        type: 'Camera',
        x: 50,
        y: 88,
        height: 5,
        rotation: 0,
        shotLabel: 'Portrait',
        lens: '85mm Prime',
        focalLength: 85
      }
    ]
  },
  {
    key: 'book-light',
    label: 'Book Light Setup',
    icon: BlurLinearIcon,
    accent: '#38bdf8',
    tags: ['Commercial', 'Soft Light'],
    keyRatio: '2:1',
    cameraSetup: 'Single cam · 40mm',
    recommendedUse: 'Beauty and product work needing ultra-soft key.',
    description: 'Ultra-soft bounced lighting through diffusion for beauty and commercial work.',
    fixtures: [
      {
        name: 'Key (Octabox)',
        type: 'Key Light',
        intensity: 88,
        x: 24,
        y: 58,
        rotation: -30,
        height: 10,
        notes: 'Aim into bounce for book light.'
      },
      {
        name: 'Bounce Board',
        type: 'Bounce Board',
        intensity: 0,
        x: 34,
        y: 54,
        rotation: -20,
        notes: 'White bounce for key source.'
      },
      {
        name: 'Diffusion Panel',
        type: 'Diffusion Panel',
        intensity: 0,
        x: 44,
        y: 52,
        rotation: -10,
        notes: '4x4 diffusion between bounce and subject.'
      },
      {
        name: 'Practical Accent',
        type: 'Practical',
        intensity: 35,
        x: 82,
        y: 62,
        rotation: 0,
        notes: 'Practical lamp in background.'
      },
      {
        name: 'Camera A',
        type: 'Camera',
        x: 52,
        y: 84,
        height: 5,
        rotation: 0,
        shotLabel: 'Shot A',
        lens: '40mm Prime',
        focalLength: 40
      }
    ]
  },
  {
    key: 'interview',
    label: 'Professional Interview',
    icon: VideocamIcon,
    accent: '#6366f1',
    tags: ['Corporate', 'Two Camera'],
    keyRatio: '3:1',
    cameraSetup: 'Dual cam · 35mm & 85mm',
    recommendedUse: 'Polished interviews and documentary sit-downs.',
    description: 'Clean two-camera setup with balanced lighting for talking head interviews.',
    fixtures: [
      {
        name: 'Key Light',
        type: 'Key Light',
        intensity: 78,
        x: 30,
        y: 52,
        rotation: -25,
        height: 12,
        notes: 'Soft key camera left.'
      },
      {
        name: 'Fill Light',
        type: 'Fill Light',
        intensity: 48,
        x: 70,
        y: 58,
        rotation: 30,
        height: 10,
        notes: 'Balanced fill camera right.'
      },
      {
        name: 'Back Light',
        type: 'Back Light',
        intensity: 65,
        x: 50,
        y: 28,
        rotation: 180,
        height: 16,
        notes: 'Hair light for separation.'
      },
      {
        name: 'Background Light',
        type: 'Ambient',
        intensity: 40,
        x: 50,
        y: 15,
        rotation: 0,
        height: 8,
        notes: 'Background wash for depth.'
      },
      {
        name: 'Camera A',
        type: 'Camera',
        x: 45,
        y: 85,
        height: 5,
        rotation: 5,
        shotLabel: 'Wide',
        lens: '35mm Prime',
        focalLength: 35
      },
      {
        name: 'Camera B',
        type: 'Camera',
        x: 55,
        y: 85,
        height: 5,
        rotation: -5,
        shotLabel: 'Close',
        lens: '85mm Prime',
        focalLength: 85
      }
    ]
  }
];

const buildFixturesFromBlueprint = (fixtures = []) => fixtures.map((config) => createFixture(config));

const createDefaultFixtures = () => buildFixturesFromBlueprint(PRESET_BLUEPRINTS[0]?.fixtures ?? []);

const TOOLBAR_BLUEPRINT_KEYS = ['three-point', 'butterfly', 'interview'];
const TOOLBAR_BLUEPRINTS = TOOLBAR_BLUEPRINT_KEYS.map((key) =>
  PRESET_BLUEPRINTS.find((blueprint) => blueprint.key === key)
).filter(Boolean);

const INDIVIDUAL_FIXTURE_TEMPLATES = [
  {
    label: 'Key Light',
    icon: LightbulbIcon,
    config: {
      name: 'Key Light',
      type: 'Key Light',
      intensity: 80,
      x: 32,
      y: 52,
      rotation: -20
    }
  },
  {
    label: 'Fill Light',
    icon: LightbulbIcon,
    config: {
      name: 'Fill Light',
      type: 'Fill Light',
      intensity: 50,
      x: 68,
      y: 58,
      rotation: 20
    }
  },
  {
    label: 'Back Light',
    icon: LightbulbIcon,
    config: {
      name: 'Back Light',
      type: 'Back Light',
      intensity: 60,
      x: 52,
      y: 28,
      rotation: 180
    }
  },
  {
    label: 'Diffusion Panel',
    icon: BlurLinearIcon,
    config: {
      name: 'Diffusion Panel',
      type: 'Diffusion Panel',
      intensity: 0,
      x: 45,
      y: 50,
      rotation: -10
    }
  },
  {
    label: 'Bounce Board',
    icon: CropLandscapeIcon,
    config: {
      name: 'Bounce Board',
      type: 'Bounce Board',
      intensity: 0,
      x: 36,
      y: 56,
      rotation: -20
    }
  },
  {
    label: 'Flag / Cutter',
    icon: FlagIcon,
    config: {
      name: 'Flag / Cutter',
      type: 'Flag / Cutter',
      intensity: 0,
      x: 70,
      y: 40,
      rotation: 10
    }
  },
  {
    label: 'Wall',
    icon: ViewWeekIcon,
    config: {
      name: 'Wall',
      type: 'Wall',
      x: 50,
      y: 40,
      rotation: 0,
      length: 16,
      height: 12,
      notes: 'Drag to place structural wall segment.'
    }
  },
  {
    label: 'Window',
    icon: WindowIcon,
    config: {
      name: 'Window',
      type: 'Window',
      x: 60,
      y: 45,
      rotation: 0,
      length: 8,
      height: 6,
      notes: 'Window opening for natural light.'
    }
  },
  {
    label: 'Door',
    icon: DoorFrontIcon,
    config: {
      name: 'Door',
      type: 'Door',
      x: 58,
      y: 46,
      rotation: 0,
      length: 3,
      height: 7,
      notes: 'Doorway opening.'
    }
  },
  {
    label: 'Practical',
    icon: LightbulbIcon,
    config: {
      name: 'Practical',
      type: 'Practical',
      intensity: 40,
      x: 78,
      y: 64,
      rotation: 0
    }
  },
  {
    label: 'Camera',
    icon: VideocamIcon,
    config: {
      name: 'Camera',
      type: 'Camera',
      intensity: 0,
      x: 50,
      y: 84,
      rotation: 0,
      shotLabel: 'Shot A',
      lens: '35mm Prime',
      focalLength: 35
    }
  },
  {
    label: 'Talent Marker',
    icon: PersonIcon,
    config: {
      name: 'Talent Marker',
      type: 'Talent Marker',
      intensity: 0,
      x: 50,
      y: 60,
      rotation: 0,
      talentName: 'Talent',
      talentHeight: 5.8
    }
  }
];

const TOOLBAR_PRIMARY_TYPES = [
  'Key Light',
  'Fill Light',
  'Back Light',
  'Practical',
  'Spot',
  'Ambient',
  'Camera'
];

const TOOLBAR_FIXTURE_TEMPLATES = TOOLBAR_PRIMARY_TYPES.map((type) =>
  INDIVIDUAL_FIXTURE_TEMPLATES.find((template) => template.config.type === type)
).filter(Boolean);

const TOOLBAR_STRUCTURE_TEMPLATES = INDIVIDUAL_FIXTURE_TEMPLATES.filter((template) =>
  STRUCTURE_TYPES.includes(template.config.type)
);

const TALENT_TEMPLATE = INDIVIDUAL_FIXTURE_TEMPLATES.find(
  (template) => template.config.type === 'Talent Marker'
);

const INITIAL_BACKDROP = {
  topPercent: 10,
  horizontalMarginPercent: 6,
  heightPercent: 46,
  color: '#f0f4ff',
  borderOpacity: 0.2,
  opacity: 0.22
};

const DEFAULT_STAGE_DIMENSIONS = {
  unit: 'ft',
  width: 30,
  height: 30,
  subdivisions: 6
};

const LENGTH_UNITS = ['ft', 'm'];

const UNIT_CONVERSIONS = {
  ft: {
    to: (value, targetUnit) => (targetUnit === 'm' ? value * 0.3048 : value)
  },
  m: {
    to: (value, targetUnit) => (targetUnit === 'ft' ? value / 0.3048 : value)
  }
};

const convertLength = (value, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return value;
  const converter = UNIT_CONVERSIONS[fromUnit];
  if (!converter) return value;
  return converter.to(value, toUnit);
};

const sanitizeStageDimensions = (input = {}) => {
  const candidate = { ...DEFAULT_STAGE_DIMENSIONS, ...(input ?? {}) };
  const unit = LENGTH_UNITS.includes(candidate.unit) ? candidate.unit : DEFAULT_STAGE_DIMENSIONS.unit;
  const widthValue = parseStageDimensionInputValue(candidate.width, unit, DEFAULT_STAGE_DIMENSIONS.width);
  const heightValue = parseStageDimensionInputValue(candidate.height, unit, DEFAULT_STAGE_DIMENSIONS.height);
  const subdivisions = Number.isFinite(Number(candidate.subdivisions)) ? Number(candidate.subdivisions) : DEFAULT_STAGE_DIMENSIONS.subdivisions;

  return {
    unit,
    width: Number(clamp(widthValue, 1, 200).toFixed(1)),
    height: Number(clamp(heightValue, 1, 200).toFixed(1)),
    subdivisions: Math.round(clamp(subdivisions, 2, 12))
  };
};

const getFixtureVisual = (fixture) => {
  const definition = FIXTURE_DEFINITIONS[fixture.type] ?? FIXTURE_DEFINITIONS.Custom;
  return definition;
};

const getFixtureStagePosition = (fixture, stageDimensions) => {
  const width = stageDimensions.width ?? DEFAULT_STAGE_DIMENSIONS.width;
  const height = stageDimensions.height ?? DEFAULT_STAGE_DIMENSIONS.height;

  return {
    x: (fixture.x / 100) * width,
    y: (fixture.y / 100) * height
  };
};

const degreesToRadians = (value) => (value * Math.PI) / 180;

const LightingDiagramTool = ({ projectId }) => {
  const theme = useTheme();
  const storageKey = useMemo(() => (projectId ? `lighting-diagram-${projectId}` : null), [projectId]);
  const initialStageDimensions = useMemo(() => sanitizeStageDimensions(), []);
  const [fixtures, setFixtures] = useState([]);
  const [selectedFixtureId, setSelectedFixtureId] = useState(null);
  const [showGrid, setShowGrid] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [stageDimensions, setStageDimensions] = useState(initialStageDimensions);
  const [stageDimensionInputs, setStageDimensionInputs] = useState(() => ({
    width: formatStageDimensionInputValue(initialStageDimensions.width, initialStageDimensions.unit),
    height: formatStageDimensionInputValue(initialStageDimensions.height, initialStageDimensions.unit),
    subdivisions: initialStageDimensions.subdivisions.toString()
  }));
  const [structureDimensionInputs, setStructureDimensionInputs] = useState({});
  const [draggingFixtureId, setDraggingFixtureId] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 1, height: 1 });
  const [backdrop, setBackdrop] = useState(INITIAL_BACKDROP);
  const [draggingBackdropHandle, setDraggingBackdropHandle] = useState(null);
  const backdropDragStartRef = useRef({ x: 0, y: 0, top: 0, height: 0, margin: 0 });
  const [savedDiagrams, setSavedDiagrams] = useState([]);
  const [saveDiagramName, setSaveDiagramName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [controlsPanelOpen, setControlsPanelOpen] = useState(true);
  const [fixturePositionInputs, setFixturePositionInputs] = useState({ x: '', y: '', rotation: '' });
  const [printScale, setPrintScale] = useState('fit'); // 'fit', '0.25', '0.5' (inches per foot)
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    position: false,
    appearance: false,
    notes: false
  });
  const [controlsPanelPosition, setControlsPanelPosition] = useState({
    x: typeof window !== 'undefined' ? window.innerWidth - 680 : 0,
    y: 600
  });
  const [isDraggingControls, setIsDraggingControls] = useState(false);
  const controlsDragOffsetRef = useRef({ x: 0, y: 0 });
  const controlsPanelRef = useRef(null);

  const clampPanelPosition = useCallback((x, y) => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
    const panelWidth = controlsPanelRef.current?.offsetWidth ?? 360;

    const minX = 16;
    const maxX = Math.max(minX, viewportWidth - panelWidth - 16);

    const minY = 32;
    const bottomMargin = 24;
    const maxY = Math.max(minY, viewportHeight - bottomMargin - 32);

    return {
      x: clamp(x, minX, maxX),
      y: clamp(y, minY, maxY)
    };
  }, []);

  const handleControlsPointerDown = useCallback((event) => {
    event.preventDefault();

    const initialX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX;
    const initialY = event.type === 'touchstart' ? event.touches[0].clientY : event.clientY;

    controlsDragOffsetRef.current = {
      x: initialX - controlsPanelPosition.x,
      y: initialY - controlsPanelPosition.y
    };

    setIsDraggingControls(true);

    const handlePointerMove = (moveEvent) => {
      const clientX = moveEvent.type.startsWith('touch') ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const clientY = moveEvent.type.startsWith('touch') ? moveEvent.touches[0].clientY : moveEvent.clientY;

      const { x, y } = clampPanelPosition(
        clientX - controlsDragOffsetRef.current.x,
        clientY - controlsDragOffsetRef.current.y
      );

      setControlsPanelPosition({ x, y });
    };

    const handlePointerUp = () => {
      setIsDraggingControls(false);
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
      window.removeEventListener('touchcancel', handlePointerUp);
    };

    window.addEventListener('mousemove', handlePointerMove, { passive: false });
    window.addEventListener('mouseup', handlePointerUp);
    window.addEventListener('touchmove', handlePointerMove, { passive: false });
    window.addEventListener('touchend', handlePointerUp);
    window.addEventListener('touchcancel', handlePointerUp);
  }, [clampPanelPosition, controlsPanelPosition.x, controlsPanelPosition.y]);
  const stageRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  const saveTimerRef = useRef(null);

  // Save to API with debouncing and localStorage backup
  const saveToAPI = useCallback(async (data) => {
    if (!projectId) return;
    
    // Always save to localStorage immediately
    try {
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(data));
      }
      localStorage.setItem('saved-lighting-diagrams', JSON.stringify(data.savedDiagrams || savedDiagrams));
    } catch (err) {
      console.error('Error saving to localStorage:', err);
    }

    // Debounce API save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      try {
        await saveLightingDiagram(projectId, data);
        console.log('✅ Lighting diagram saved to API');
      } catch (err) {
        console.error('Error saving lighting diagram to API (localStorage backup active):', err);
      }
    }, 1500);
  }, [projectId, storageKey, savedDiagrams]);

  useEffect(() => {
    const defaults = createDefaultFixtures();
    const fallbackStage = sanitizeStageDimensions();

    if (!storageKey) {
      setFixtures(defaults);
      setSelectedFixtureId(defaults[0]?.id ?? null);
      setStageDimensions(fallbackStage);
      setStageDimensionInputs({
        width: formatStageDimensionInputValue(fallbackStage.width, fallbackStage.unit),
        height: formatStageDimensionInputValue(fallbackStage.height, fallbackStage.unit),
        subdivisions: fallbackStage.subdivisions.toString()
      });
      setHasLoaded(true);
      return;
    }

    const loadDiagram = async () => {
      try {
        // Try API first
        const response = await getLightingDiagram(projectId);
        if (response?.data?.diagram) {
          const diagram = response.data.diagram;
          if (Array.isArray(diagram.fixtures)) {
            setFixtures(diagram.fixtures);
            setSelectedFixtureId(diagram.selectedFixtureId ?? diagram.fixtures[0]?.id ?? null);
            setShowGrid(diagram.showGrid ?? true);
            if (diagram.backdrop) {
              setBackdrop({ ...INITIAL_BACKDROP, ...diagram.backdrop });
            }
            if (Array.isArray(diagram.savedDiagrams)) {
              setSavedDiagrams(diagram.savedDiagrams);
            }
            const restoredStage = sanitizeStageDimensions(diagram.stageDimensions);
            setStageDimensions(restoredStage);
            setStageDimensionInputs({
              width: formatStageDimensionInputValue(restoredStage.width, restoredStage.unit),
              height: formatStageDimensionInputValue(restoredStage.height, restoredStage.unit),
              subdivisions: restoredStage.subdivisions.toString()
            });
            console.log('✅ Loaded lighting diagram from API');
            setHasLoaded(true);
            return;
          }
        }
        throw new Error('No data from API');
      } catch (err) {
        console.log('🔍 No API data, checking localStorage...');
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem(storageKey);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed.fixtures)) {
              setFixtures(parsed.fixtures);
              setSelectedFixtureId(parsed.selectedFixtureId ?? parsed.fixtures[0]?.id ?? null);
              setShowGrid(parsed.showGrid ?? true);
              if (parsed.backdrop) {
                setBackdrop({ ...INITIAL_BACKDROP, ...parsed.backdrop });
              }
              const restoredStage = sanitizeStageDimensions(parsed.stageDimensions);
              setStageDimensions(restoredStage);
              setStageDimensionInputs({
                width: formatStageDimensionInputValue(restoredStage.width, restoredStage.unit),
                height: formatStageDimensionInputValue(restoredStage.height, restoredStage.unit),
                subdivisions: restoredStage.subdivisions.toString()
              });
              // Migrate to API
              setTimeout(() => {
                saveLightingDiagram(projectId, parsed)
                  .then(() => console.log('✅ Migrated lighting diagram to API'))
                  .catch(e => console.log('Migration pending:', e.message));
              }, 2000);
              setHasLoaded(true);
              return;
            }
          }
        } catch (localErr) {
          console.error('Failed to load from localStorage:', localErr);
        }
      }

      setFixtures(defaults);
      setSelectedFixtureId(defaults[0]?.id ?? null);
      setStageDimensions(fallbackStage);
      setBackdrop(INITIAL_BACKDROP);
      setStageDimensionInputs({
        width: formatStageDimensionInputValue(fallbackStage.width, fallbackStage.unit),
        height: formatStageDimensionInputValue(fallbackStage.height, fallbackStage.unit),
        subdivisions: fallbackStage.subdivisions.toString()
      });
      setHasLoaded(true);
    };

    loadDiagram();
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [storageKey, projectId]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('saved-lighting-diagrams');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSavedDiagrams(parsed);
        }
      }
    } catch (error) {
      console.error('Failed to load saved diagrams', error);
    }
  }, []);

  useEffect(() => {
    if (!storageKey || !hasLoaded) return;

    const data = { fixtures, selectedFixtureId, showGrid, stageDimensions, backdrop, savedDiagrams };
    saveToAPI(data);
  }, [fixtures, selectedFixtureId, showGrid, stageDimensions, backdrop, storageKey, hasLoaded, saveToAPI, savedDiagrams]);

  useEffect(() => {
    setStageDimensionInputs((prev) => {
      const next = {
        width: formatStageDimensionInputValue(stageDimensions.width, stageDimensions.unit),
        height: formatStageDimensionInputValue(stageDimensions.height, stageDimensions.unit),
        subdivisions: stageDimensions.subdivisions.toString()
      };
      if (
        prev.width === next.width &&
        prev.height === next.height &&
        prev.subdivisions === next.subdivisions
      ) {
        return prev;
      }
      return next;
    });
  }, [stageDimensions.height, stageDimensions.subdivisions, stageDimensions.width]);

  useEffect(() => {
    setStructureDimensionInputs(() => {
      const next = {};
      fixtures.forEach((fixture) => {
        if (!STRUCTURE_TYPES.includes(fixture.type)) {
          return;
        }

        next[fixture.id] = {
          length: formatStructureInputValue(fixture.length, stageDimensions.unit)
        };
      });
      return next;
    });
  }, [fixtures, stageDimensions.unit]);

  const selectedFixture = fixtures.find((fixture) => fixture.id === selectedFixtureId) ?? null;

  useEffect(() => {
    if (!selectedFixture) {
      setFixturePositionInputs({ x: '', y: '', rotation: '' });
      return;
    }

    setFixturePositionInputs({
      x: Number.isFinite(selectedFixture.x) ? formatDecimalValue(selectedFixture.x, 1) : '',
      y: Number.isFinite(selectedFixture.y) ? formatDecimalValue(selectedFixture.y, 1) : '',
      rotation: Number.isFinite(selectedFixture.rotation) ? formatDecimalValue(selectedFixture.rotation, 1) : ''
    });
  }, [selectedFixture]);

  useEffect(() => {
    if (!stageRef.current) return undefined;

    const measure = () => {
      if (!stageRef.current) return;
      const rect = stageRef.current.getBoundingClientRect();
      const width = rect.width || 1;
      const height = rect.height || 1;
      setStageSize((prev) => {
        if (prev.width === width && prev.height === height) {
          return prev;
        }
        return { width, height };
      });
    };

    measure();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => measure());
      observer.observe(stageRef.current);
      return () => observer?.disconnect();
    }

    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    if (!stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    setStageSize({ width: rect.width || 1, height: rect.height || 1 });
  }, [stageDimensions.width, stageDimensions.height, stageDimensions.subdivisions]);

  const getPointerPercent = useCallback((event) => {
    if (!stageRef.current) return { x: 50, y: 50 };
    const rect = stageRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    return { x: clamp(x, 0, 100), y: clamp(y, 0, 100) };
  }, []);

  const handleMouseMove = (event) => {
    if (!draggingFixtureId || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 1, 99);
    const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 1, 99);
    return { x: clamp(x, 0, 100), y: clamp(y, 0, 100) };
  };

  const handleAddFixture = (config) => {
    const fixture = createFixture(config);
    setFixtures((prev) => [...prev, fixture]);
    setSelectedFixtureId(fixture.id);
  };

  const handleBackdropHandlePointerDown = useCallback((event, handle) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    const { x, y } = getPointerPercent(event);
    backdropDragStartRef.current = {
      x,
      y,
      top: backdrop.topPercent,
      height: backdrop.heightPercent,
      margin: backdrop.horizontalMarginPercent
    };
    setDraggingBackdropHandle(handle);
  }, [getPointerPercent, backdrop]);

  useEffect(() => {
    if (!draggingBackdropHandle) return;

    const handleMouseMove = (event) => {
      const { x, y } = getPointerPercent(event);
      const start = backdropDragStartRef.current;
      const deltaY = y - start.y;
      const deltaX = x - start.x;

      setBackdrop((prev) => {
        let next = { ...prev };

        if (draggingBackdropHandle === 'top') {
          const newTop = clamp(start.top + deltaY, 0, 100 - prev.heightPercent);
          next.topPercent = newTop;
        } else if (draggingBackdropHandle === 'bottom') {
          const maxHeight = 100 - prev.topPercent;
          const newHeight = clamp(start.height + deltaY, 5, maxHeight);
          next.heightPercent = newHeight;
        } else if (draggingBackdropHandle === 'left') {
          const newMargin = clamp(start.margin + deltaX, 0, 45);
          next.horizontalMarginPercent = newMargin;
        } else if (draggingBackdropHandle === 'right') {
          const newMargin = clamp(start.margin - deltaX, 0, 45);
          next.horizontalMarginPercent = newMargin;
        }

        return next;
      });
    };

    const handleMouseUp = () => {
      setDraggingBackdropHandle(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp, { once: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingBackdropHandle, getPointerPercent]);

  const handleFixturePointerDown = useCallback((event, fixture) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    setSelectedFixtureId(fixture.id);
    const { x, y } = getPointerPercent(event);
    dragOffsetRef.current = {
      x: x - fixture.x,
      y: y - fixture.y
    };
    setDraggingFixtureId(fixture.id);
  }, [getPointerPercent]);

  const handleStageUnitToggle = (_, nextUnit) => {
    if (!nextUnit || nextUnit === stageDimensions.unit) return;
    setStageDimensions((prev) => {
      const convertedWidth = convertLength(prev.width, prev.unit, nextUnit);
      const convertedHeight = convertLength(prev.height, prev.unit, nextUnit);
      return sanitizeStageDimensions({
        ...prev,
        unit: nextUnit,
        width: convertedWidth,
        height: convertedHeight
      });
    });
  };

  const handleStageDimensionInputChange = (field, rawValue) => {
    setStageDimensionInputs((prev) => ({ ...prev, [field]: rawValue }));
  };

  const commitStageDimensionInput = (field) => {
    const rawValue = stageDimensionInputs[field];
    let numeric;

    if (field === 'subdivisions') {
      numeric = Number.parseInt(rawValue, 10);
    } else {
      numeric = parseStageDimensionInputValue(rawValue, stageDimensions.unit, stageDimensions[field]);
    }

    if (!Number.isFinite(numeric)) {
      numeric = stageDimensions[field];
    }

    const sanitized = sanitizeStageDimensions({
      ...stageDimensions,
      [field]: numeric
    });

    setStageDimensions(sanitized);
    setStageDimensionInputs({
      width: formatStageDimensionInputValue(sanitized.width, sanitized.unit),
      height: formatStageDimensionInputValue(sanitized.height, sanitized.unit),
      subdivisions: sanitized.subdivisions.toString()
    });
  };

  const handleStageDimensionInputBlur = (field) => {
    commitStageDimensionInput(field);
  };

  const handleStageDimensionInputKeyDown = (event, field) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitStageDimensionInput(field);
      event.currentTarget.blur();
    }
  };

  const getStructureLengthInputValue = (fixture) => {
    if (!fixture) return '';
    const unit = stageDimensions.unit;
    const stored = structureDimensionInputs[fixture.id]?.length;
    if (typeof stored === 'string') return stored;
    const numeric = fixture.length;
    return formatStructureInputValue(numeric, unit);
  };

  const handleStructureDimensionInputChange = (fixtureId, field, rawValue) => {
    if (field !== 'length') return;

    setStructureDimensionInputs((prev) => ({
      ...prev,
      [fixtureId]: {
        ...(prev[fixtureId] ?? {}),
        length: rawValue
      }
    }));

    const fixture = fixtures.find((item) => item.id === fixtureId);
    if (!fixture || rawValue.trim() === '') {
      return;
    }

    const unit = stageDimensions.unit;
    const parsed = parseStructureInputValue(rawValue, unit, fixture.length);
    if (!Number.isFinite(parsed)) {
      return;
    }

    const maxStructureLength = stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width;
    const minStructureLength = getStructureMinLength(stageDimensions.unit);
    const sanitizedValue = clamp(parsed, minStructureLength, maxStructureLength);

    handleUpdateFixture('length', sanitizedValue, {
      fixtureId,
      skipStructureInputSync: true
    });
  };

  const commitStructureDimensionInput = (fixture, field) => {
    if (!fixture || field !== 'length') return;

    const unit = stageDimensions.unit;
    const stored = structureDimensionInputs[fixture.id]?.length;
    const fallback = fixture.length;
    const parsed = parseStructureInputValue(stored, unit, fallback);

    if (!Number.isFinite(parsed)) {
      setStructureDimensionInputs((prev) => ({
        ...prev,
        [fixture.id]: {
          ...(prev[fixture.id] ?? {}),
          length: formatStructureInputValue(fixture.length, unit)
        }
      }));
      return;
    }

    const maxStructureLength = stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width;
    const minStructureLength = getStructureMinLength(stageDimensions.unit);
    const sanitizedValue = clamp(parsed, minStructureLength, maxStructureLength);

    setStructureDimensionInputs((prev) => ({
      ...prev,
      [fixture.id]: {
        ...(prev[fixture.id] ?? {}),
        length: formatStructureInputValue(sanitizedValue, unit)
      }
    }));

    handleUpdateFixture('length', sanitizedValue, {
      fixtureId: fixture.id,
      skipStructureInputSync: true
    });
  };

  const handleStructureDimensionInputBlur = (fixture, field) => {
    if (field !== 'length') return;
    commitStructureDimensionInput(fixture, field);
  };

  const handleStructureDimensionInputKeyDown = (event, fixture, field) => {
    if (field !== 'length') return;
    if (event.key === 'Enter') {
      event.preventDefault();
      commitStructureDimensionInput(fixture, field);
      event.currentTarget.blur();
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFixturePositionInputChange = (field, value) => {
    setFixturePositionInputs((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetFixturePositionInputField = (field) => {
    if (!selectedFixture) return;
    setFixturePositionInputs((prev) => ({
      ...prev,
      [field]: Number.isFinite(selectedFixture[field]) ? formatDecimalValue(selectedFixture[field], 1) : ''
    }));
  };

  const commitFixturePositionInput = (field) => {
    if (!selectedFixture) return;
    const rawValue = fixturePositionInputs[field];
    if (rawValue === '' || rawValue == null) {
      resetFixturePositionInputField(field);
      return;
    }

    const parsed = Number.parseFloat(rawValue);
    if (!Number.isFinite(parsed)) {
      resetFixturePositionInputField(field);
      return;
    }

    const clamped = field === 'rotation' ? clamp(parsed, -180, 180) : clamp(parsed, 1, 99);
    handleUpdateFixture(field, clamped);
  };

  const handleFixturePositionInputBlur = (field) => {
    commitFixturePositionInput(field);
  };

  const handleFixturePositionInputKeyDown = (event, field) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitFixturePositionInput(field);
      event.currentTarget.blur();
    }
  };

  const handleUpdateFixture = (field, value, options = {}) => {
    const targetFixtureId = options.fixtureId ?? selectedFixtureId;
    if (!targetFixtureId) return;

    const maxStructureLength = stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width;
    const minStructureLength = getStructureMinLength(stageDimensions.unit);

    setFixtures((prev) =>
      prev.map((fixture) => {
        if (fixture.id !== targetFixtureId) return fixture;

        if (field === 'type') {
          return createFixture({ ...fixture, type: value });
        }

        let nextValue = value;
        const numericFields = ['beamAngle', 'rotation', 'height', 'length', 'x', 'y', 'focalLength', 'talentHeight'];

        if (numericFields.includes(field)) {
          const parsed = typeof value === 'string' ? parseFloat(value) : value;
          nextValue = Number.isFinite(parsed) ? parsed : fixture[field];
        }

        if (field === 'x' || field === 'y') {
          nextValue = clamp(nextValue, 1, 99);
          if (STRUCTURE_TYPES.includes(fixture.type)) {
            nextValue = snapStructureAxisToNeighbors(
              nextValue,
              field,
              fixture.id,
              prev,
              stageDimensions,
              2
            );
          }
        }

        if (field === 'rotation') {
          nextValue = clamp(nextValue, -180, 180);
        }

        if (field === 'height') {
          const minHeight = STRUCTURE_TYPES.includes(fixture.type) ? 1 : 5;
          nextValue = clamp(nextValue, minHeight, 30);
        }

        if (field === 'length') {
          nextValue = clamp(nextValue, minStructureLength, maxStructureLength);
        }

        if (field === 'focalLength') {
          nextValue = clamp(nextValue, 8, 200);
        }

        if (field === 'talentHeight') {
          nextValue = clamp(nextValue, 3, 7.5);
        }

        let updatedFixture = {
          ...fixture,
          [field]: nextValue
        };

        if (STRUCTURE_TYPES.includes(updatedFixture.type)) {
          const snapped = snapStructurePosition(
            updatedFixture.x,
            updatedFixture.y,
            updatedFixture,
            prev,
            stageDimensions,
            1.5
          );

          updatedFixture = {
            ...updatedFixture,
            x: snapped.x,
            y: snapped.y,
            height: 1
          };

          if (field !== 'height') {
            nextValue = updatedFixture[field];
          }
        }

        return updatedFixture;
      })
    );
  };

  const handleDuplicateFixture = (fixtureId) => {
    const original = fixtures.find((fixture) => fixture.id === fixtureId);
    if (!original) return;

    const duplicate = createFixture({
      ...original,
      id: undefined,
      name: `${original.name} Copy`,
      x: clamp(original.x + 4, 5, 95),
      y: clamp(original.y + 4, 5, 95)
    });

    setFixtures((prev) => [...prev, duplicate]);
    setSelectedFixtureId(duplicate.id);
  };

  const handleDeleteFixture = (fixtureId) => {
    setFixtures((prev) => {
      const next = prev.filter((fixture) => fixture.id !== fixtureId);
      if (fixtureId === selectedFixtureId) {
        setSelectedFixtureId(next[0]?.id ?? null);
      }
      return next;
    });
  };

  const handleApplyBlueprint = (blueprint) => {
    const defaults = buildFixturesFromBlueprint(blueprint.fixtures ?? []);
    setFixtures(defaults);
    setSelectedFixtureId(defaults[0]?.id ?? null);
  };

  const handleResetDiagram = () => {
    handleApplyBlueprint(PRESET_BLUEPRINTS[0]);
  };

  const handlePrintDiagram = () => {
    try {
      const stageElement = stageRef.current;
      if (!stageElement) return;

      // Clone the stage contents into a dedicated print container so CSS can target it cleanly
      const stageClone = stageElement.cloneNode(true);
      // Remove overlay chrome like dashed guides so only fixtures render
      stageClone.querySelectorAll('[data-print-hidden], .stage-overlay-border').forEach((el) => el?.remove());
      stageClone.id = 'printing-stage';
      stageClone.style.margin = '0';
      const printWrapper = document.createElement('div');
      printWrapper.id = 'printing-stage-wrapper';
      printWrapper.appendChild(stageClone);
      document.body.appendChild(printWrapper);

      const style = document.createElement('style');
      style.setAttribute('data-print-style', 'diagram-only');

      const pageWidthIn = 11;
      const pageHeightIn = 8.5;
      const marginIn = 0.25;
      const printableWIn = pageWidthIn - 2 * marginIn;
      const printableHIn = pageHeightIn - 2 * marginIn;

      // Use actual stage dimensions so proportions match the live diagram
      const stageRect = stageElement.getBoundingClientRect();
      const layoutWidthPx = stageRect.width || stageElement.offsetWidth || stageSize.width || 1200;
      const layoutHeightPx = stageRect.height || stageElement.offsetHeight || stageSize.height || 900;
      stageClone.style.width = `${layoutWidthPx}px`;
      stageClone.style.height = `${layoutHeightPx}px`;
      stageClone.style.maxWidth = 'none';
      stageClone.style.maxHeight = 'none';
      stageClone.style.aspectRatio = 'auto';
      const stageBg = window.getComputedStyle(stageElement).backgroundColor || '#111';
      stageClone.style.background = stageBg;
      stageClone.style.color = window.getComputedStyle(stageElement).color || '#fff';
      const PX_PER_IN = 96;
      const layoutWidthIn = layoutWidthPx / PX_PER_IN;
      const layoutHeightIn = layoutHeightPx / PX_PER_IN;
      const scaleFactor = Math.min(
        printableWIn / layoutWidthIn,
        printableHIn / layoutHeightIn
      );

      style.innerHTML = `
        @page { size: letter landscape; margin: ${marginIn}in; }
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            background: ${stageBg} !important;
          }
          body * {
            visibility: hidden !important;
          }
          #printing-stage-wrapper,
          #printing-stage-wrapper * {
            visibility: visible !important;
          }
          #printing-stage-wrapper {
            position: fixed !important;
            inset: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: ${marginIn}in !important;
            background: ${stageBg} !important;
          }
          #printing-stage {
            width: ${layoutWidthPx}px !important;
            height: ${layoutHeightPx}px !important;
            border: none !important;
            border-radius: 8px !important;
            overflow: hidden !important;
            background: ${stageBg} !important;
            color: inherit !important;
            box-shadow: none !important;
            transform: scale(${scaleFactor}) !important;
            transform-origin: center center !important;
          }
          #printing-stage * {
            color: inherit !important;
            fill: inherit !important;
          }
          #printing-stage .MuiTypography-root,
          #printing-stage .MuiButton-root,
          #printing-stage .MuiSvgIcon-root {
            color: inherit !important;
            fill: inherit !important;
          }
        }
      `;

      document.head.appendChild(style);
      window.print();
      setTimeout(() => {
        if (style.parentNode) style.parentNode.removeChild(style);
        if (printWrapper.parentNode) printWrapper.parentNode.removeChild(printWrapper);
      }, 1000);
    } catch (e) {
      console.error('Print failed', e);
    }
  };

  const handleExportDiagram = () => {
    const exportData = {
      version: '1.0',
      created: new Date().toISOString(),
      stageDimensions,
      fixtures
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `lighting-diagram-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSaveDiagram = () => {
    if (!saveDiagramName.trim()) {
      alert('Please enter a name for the diagram.');
      return;
    }

    const newDiagram = {
      id: generateFixtureId(),
      name: saveDiagramName.trim(),
      created: new Date().toISOString(),
      stageDimensions,
      fixtures,
      fixtureCount: fixtures.length
    };

    const updated = [...savedDiagrams, newDiagram];
    setSavedDiagrams(updated);
    localStorage.setItem('saved-lighting-diagrams', JSON.stringify(updated));
    setSaveDiagramName('');
    setShowSaveDialog(false);
  };

  const handleLoadDiagram = (diagram) => {
    setFixtures(diagram.fixtures);
    setSelectedFixtureId(diagram.fixtures[0]?.id ?? null);
    setStageDimensions(sanitizeStageDimensions(diagram.stageDimensions));
  };

  const handleDeleteSavedDiagram = (diagramId) => {
    const updated = savedDiagrams.filter((d) => d.id !== diagramId);
    setSavedDiagrams(updated);
    localStorage.setItem('saved-lighting-diagrams', JSON.stringify(updated));
  };

  useEffect(() => {
    if (!draggingFixtureId) return;

    const handleMouseMove = (event) => {
      const { x, y } = getPointerPercent(event);
      setFixtures((prev) =>
        prev.map((fixture) => {
          if (fixture.id !== draggingFixtureId) return fixture;

          const rawX = clamp(x - dragOffsetRef.current.x, -20, 120);
          const rawY = clamp(y - dragOffsetRef.current.y, -20, 120);
          let snappedX = rawX;
          let snappedY = rawY;

          if (STRUCTURE_TYPES.includes(fixture.type)) {
            snappedX = snapStructureAxisToNeighbors(rawX, 'x', fixture.id, prev, stageDimensions, 2);
            snappedY = snapStructureAxisToNeighbors(rawY, 'y', fixture.id, prev, stageDimensions, 2);

            const snappedPosition = snapStructurePosition(
              snappedX,
              snappedY,
              { ...fixture, x: snappedX, y: snappedY },
              prev,
              stageDimensions,
              1.5
            );

            snappedX = snappedPosition.x;
            snappedY = snappedPosition.y;
          }

          return {
            ...fixture,
            x: snappedX,
            y: snappedY
          };
        })
      );
    };

    const handleMouseUp = () => {
      setDraggingFixtureId(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp, { once: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingFixtureId, stageDimensions.subdivisions]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.key === 'Backspace' || event.key === 'Delete') && selectedFixtureId) {
        // Don't delete if user is typing in an input field
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }
        event.preventDefault();
        handleDeleteFixture(selectedFixtureId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFixtureId, handleDeleteFixture]);

  const gridCellSize = useMemo(() => {
    const base = 40;
    const subdivisions = stageDimensions.subdivisions || DEFAULT_STAGE_DIMENSIONS.subdivisions;
    return Math.max(20, base * (DEFAULT_STAGE_DIMENSIONS.subdivisions / subdivisions));
  }, [stageDimensions.subdivisions]);

  const stageBackgroundStyles = useMemo(() => {
    if (!showGrid) {
      return {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.4)
            : alpha(theme.palette.background.default, 0.8)
      };
    }

    const subdivisions = stageDimensions.subdivisions || DEFAULT_STAGE_DIMENSIONS.subdivisions;
    const cell = gridCellSize;
    const stageWidthPx = cell * subdivisions;
    const stageHeightPx = cell * subdivisions;
    const minor = alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.4 : 0.3);
    const major = alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.7 : 0.55);

    return {
      backgroundImage: `
        repeating-linear-gradient(0deg, ${minor}, ${minor} 1px, transparent 1px, transparent ${cell}px),
        repeating-linear-gradient(90deg, ${minor}, ${minor} 1px, transparent 1px, transparent ${cell}px),
        linear-gradient(0deg, ${major}, ${major} 1px, transparent 1px),
        linear-gradient(90deg, ${major}, ${major} 1px, transparent 1px)
      `,
      backgroundSize: `${cell}px ${cell}px, ${cell}px ${cell}px, ${stageWidthPx}px ${stageHeightPx}px, ${stageWidthPx}px ${stageHeightPx}px`,
      backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.18 : 0.65)
    };
  }, [gridCellSize, showGrid, stageDimensions.subdivisions, theme.palette.background.default, theme.palette.divider, theme.palette.mode]);

  const stageDimensionSummary = `${formatDimensionLabel(stageDimensions.width, stageDimensions.unit)} × ${formatDimensionLabel(stageDimensions.height, stageDimensions.unit)}`;

  const renderedFixtures = useMemo(() => {
    return fixtures.map((fixture) => {
      const isSelected = fixture.id === selectedFixtureId;
      const definition = getFixtureVisual(fixture);
      const shape = definition.shape ?? 'round';
      const isPanel = shape === 'panel';
      const isCamera = shape === 'camera';
      const isStructure = shape === 'structure';
      const isTalent = fixture.type === 'Talent Marker';
      const isBeamCapable = !isStructure && !isCamera && !isTalent && !isPanel;
      const beamStyle = definition.beamStyle || 'soft';
      const stageWidthUnits = stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width;
      const structureLengthPx = isStructure ? Math.max(24, (fixture.length / stageWidthUnits) * stageSize.width) : 0;
      const bounceBoardLengthPx = fixture.type === 'Bounce Board' || fixture.type === 'Diffusion Panel' || fixture.type === 'Flag / Cutter' ? Math.max(24, (fixture.length / stageWidthUnits) * stageSize.width) : 0;
      const width = isPanel ? (fixture.type === 'Bounce Board' || fixture.type === 'Diffusion Panel' || fixture.type === 'Flag / Cutter' ? bounceBoardLengthPx : 72) : isCamera ? 38 : isStructure ? structureLengthPx : 34;
      const height = isPanel ? (fixture.type === 'Bounce Board' || fixture.type === 'Diffusion Panel' || fixture.type === 'Flag / Cutter' ? 6 : 22) : isCamera ? 28 : isStructure ? Math.max(12, fixture.height * 3) : 34;
      const borderRadius = isPanel ? 12 : isCamera ? 10 : isStructure ? 0 : '50%';
      const fallbackBackground = theme.palette.background.paper;
      const structureColor = isStructure ? fixture.color ?? definition.swatch ?? fallbackBackground : null;
      const fixtureColor = !isStructure ? fixture.color ?? definition.swatch ?? fallbackBackground : null;
      const backgroundBase = isStructure ? structureColor : fixtureColor ?? definition.swatch ?? fallbackBackground;
      const fallbackAccent = definition.accent ?? theme.palette.primary.main;
      const accentColor = isStructure ? structureColor ?? fallbackAccent : fixtureColor ?? fallbackAccent;
      const backgroundColor = isSelected
        ? alpha(structureColor ?? accentColor, isPanel || isCamera || isStructure ? 0.95 : 1)
        : alpha(backgroundBase, isPanel || isCamera || isStructure ? 0.85 : 1);
      const borderColor = isStructure
        ? alpha(structureColor ?? accentColor, isSelected ? 1 : 0.6)
        : isSelected
          ? accentColor
          : alpha(accentColor, 0.6);
      const iconColor = isStructure
        ? theme.palette.getContrastText(backgroundBase)
        : fixture.color
          ? theme.palette.getContrastText(fixture.color)
          : definition.iconColor ?? (isSelected ? theme.palette.common.white : theme.palette.text.primary);

      // Enhanced beam calculations with better proportions
      const beamAngle = clamp(fixture.beamAngle ?? 45, 10, 120);
      const beamHalfAngleRad = (beamAngle * Math.PI) / 360;
      const beamLengthPx = Math.max(stageSize.height * 0.28, height * 2.8);
      const beamWidthPx = Math.max(width * 1.2, 2 * Math.tan(beamHalfAngleRad) * beamLengthPx);

      // Camera view cone with real-world focal length based FOV (full-frame)
      const focalLength = fixture.focalLength ?? 35; // default 35mm
      
      // Real FOV calculation: 2 * arctan(sensor_diagonal / (2 * focal_length))
      // Full frame sensor: 36mm x 24mm, diagonal = 43.27mm
      // Horizontal FOV (most commonly referenced for framing):
      const sensorWidth = 36; // mm
      const fovAngle = 2 * Math.atan(sensorWidth / (2 * focalLength)) * (180 / Math.PI);
      
      // Convert FOV angle to cone width percentage
      // At coneLength distance, cone width = 2 * coneLength * tan(fovAngle/2)
      const fovTopWidth = Math.min(48, Math.max(2, (fovAngle / 180) * 100));
      
      const coneLengthPx = Math.max(stageSize.height * 0.3, height * 3);
      // Cone width varies with focal length - wider for wide lenses
      const coneWidthPx = Math.max(
        stageSize.width * 0.08,
        (focalLength < 35 ? width * 3.5 : focalLength < 85 ? width * 2.2 : width * 1.2)
      );
      const cameraBodyOffset = isCamera ? height / 2 : 0;

      // Beam gradient based on beam style
      const getBeamGradient = () => {
        const baseOpacity = isSelected ? 0.75 : 0.6;
        switch (beamStyle) {
          case 'hard':
            return `radial-gradient(ellipse at top, ${alpha(accentColor, baseOpacity)} 0%, ${alpha(accentColor, baseOpacity * 0.8)} 40%, ${alpha(accentColor, 0)} 75%)`;
          case 'spot':
            return `radial-gradient(ellipse at top, ${alpha(accentColor, baseOpacity * 1.2)} 0%, ${alpha(accentColor, baseOpacity * 0.7)} 50%, ${alpha(accentColor, 0)} 80%)`;
          case 'soft':
          case 'diffuse':
            return `radial-gradient(ellipse at top, ${alpha(accentColor, baseOpacity * 0.9)} 0%, ${alpha(accentColor, baseOpacity * 0.5)} 60%, ${alpha(accentColor, 0)} 90%)`;
          case 'rim':
            return `linear-gradient(180deg, ${alpha(accentColor, baseOpacity * 0.7)} 0%, ${alpha(accentColor, baseOpacity)} 30%, ${alpha(accentColor, 0)} 100%)`;
          default:
            return `radial-gradient(ellipse at top, ${alpha(accentColor, baseOpacity)} 0%, ${alpha(accentColor, 0)} 70%)`;
        }
      };

      const hoverTransform = isStructure
        ? `translate(-50%, -50%) rotate(${fixture.rotation}deg)`
        : `translate(-50%, -50%) rotate(${fixture.rotation}deg) scale(1.08)`;

      // Get fixture icon
      const FixtureIcon = definition.icon;

      return (
        <Box
          key={fixture.id}
          sx={{
            position: 'absolute',
            left: `${fixture.x}%`,
            top: `${fixture.y}%`,
            transformOrigin: 'center'
          }}
        >
          {/* Camera View Cone */}
          {isCamera ? (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: 0,
                height: 0,
                transform: `rotate(${fixture.rotation}deg)`
              }}
            >
              {/* Camera FOV Cone - camera at narrow tip, cone extends outward */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: coneWidthPx,
                  height: coneLengthPx,
                  transform: `translate(-50%, 0)`,
                  clipPath: `polygon(50% 0%, ${50 + fovTopWidth}% 100%, ${50 - fovTopWidth}% 100%)`,
                  background: `linear-gradient(180deg, ${alpha(accentColor, isSelected ? 0.6 : 0.45)}, ${alpha(accentColor, 0.25)})`,
                  borderLeft: `4px solid ${alpha(accentColor, isSelected ? 1 : 0.85)}`,
                  borderRight: `4px solid ${alpha(accentColor, isSelected ? 1 : 0.85)}`,
                  borderBottom: `4px solid ${alpha(accentColor, isSelected ? 1 : 0.9)}`,
                  opacity: 1,
                  pointerEvents: 'auto',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '33%',
                    left: '15%',
                    right: '15%',
                    height: '2px',
                    background: `linear-gradient(90deg, transparent, ${alpha(accentColor, 0.5)}, transparent)`,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '66%',
                    left: '30%',
                    right: '30%',
                    height: '2px',
                    background: `linear-gradient(90deg, transparent, ${alpha(accentColor, 0.4)}, transparent)`,
                  }
                }}
                onMouseDown={(event) => handleFixturePointerDown(event, fixture)}
              />
              
              {/* Center focus line - from camera extending outward */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: '2px',
                  height: coneLengthPx,
                  transform: `translate(-50%, 0)`,
                  background: `linear-gradient(180deg, ${alpha(accentColor, 0.95)}, ${alpha(accentColor, 0.4)})`,
                  pointerEvents: 'none'
                }}
              />

              {/* Camera Base/Body - at origin, cone extends from it */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 36,
                  height: 26,
                  transform: 'translate(-50%, -50%)',
                  bgcolor: alpha(theme.palette.grey[800], 0.95),
                  borderRadius: '8px',
                  border: `2px solid ${alpha(accentColor, 0.8)}`,
                  boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.5)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}`,
                  pointerEvents: 'auto',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}
                onMouseDown={(event) => handleFixturePointerDown(event, fixture)}
              >
                {/* Camera Grip texture */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 6,
                    height: 18,
                    borderRadius: '2px',
                    background: `repeating-linear-gradient(0deg, ${alpha(theme.palette.common.black, 0.3)} 0px, ${alpha(theme.palette.common.black, 0.3)} 2px, transparent 2px, transparent 4px)`
                  }}
                />
                
                {/* Lens Mount Ring */}
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.grey[700], 0.9),
                    border: `2px solid ${alpha(theme.palette.grey[500], 0.6)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  {/* Lens Glass */}
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: `radial-gradient(circle at 35% 35%, ${alpha(theme.palette.common.white, 0.9)} 0%, ${alpha(accentColor, 0.5)} 30%, ${alpha(theme.palette.common.black, 0.7)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                      boxShadow: `0 0 10px ${alpha(accentColor, 0.4)}, inset 0 0 4px ${alpha(theme.palette.common.black, 0.3)}`
                    }}
                  />
                  
                  {/* Primary reflection */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '25%',
                      right: '20%',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.common.white, 0.7),
                      filter: 'blur(0.5px)'
                    }}
                  />
                  
                  {/* Secondary reflection */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '30%',
                      left: '25%',
                      width: 2,
                      height: 2,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.common.white, 0.4)
                    }}
                  />
                </Box>

                {/* Mode Dial */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -3,
                    right: 8,
                    width: 10,
                    height: 5,
                    borderRadius: '2px 2px 0 0',
                    bgcolor: alpha(theme.palette.grey[600], 0.8),
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.4)}`
                  }}
                />

                {/* Viewfinder hump */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 12,
                    height: 6,
                    borderRadius: '4px 4px 0 0',
                    bgcolor: alpha(theme.palette.grey[700], 0.9),
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.4)}`
                  }}
                />
              </Box>

              {/* Recording indicator with pulsing animation */}
              {isSelected && (
                <>
                  {/* Outer ring glow */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, calc(-50% - 28px))',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: alpha('#ef4444', 0.2),
                      animation: 'pulse 1.5s ease-in-out infinite',
                      zIndex: 11
                    }}
                  />
                  {/* Core red dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, calc(-50% - 24px))',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#ef4444',
                      boxShadow: '0 0 8px #ef4444, 0 0 16px #ef4444',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      zIndex: 11
                    }}
                  />
                </>
              )}

              {/* Focal length badge */}
              {(isSelected || fixture.focalLength) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, calc(-50% - 52px))',
                    padding: '2px 8px',
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    border: `1px solid ${alpha(accentColor, 0.5)}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 11
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      color: theme.palette.text.primary
                    }}
                  >
                    {focalLength}mm
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      color: theme.palette.text.secondary,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {focalLength < 24 ? 'Wide' : focalLength < 50 ? 'Std' : focalLength < 85 ? 'Med' : 'Tele'}
                  </Typography>
                </Box>
              )}

              {/* Shot label above camera when selected */}
              {isSelected && fixture.shotLabel && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: `translate(-50%, calc(-50% - ${fixture.focalLength ? 72 : 40}px))`,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    border: `1px solid ${alpha(accentColor, 0.4)}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                    zIndex: 11
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      color: theme.palette.text.primary,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {fixture.shotLabel}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}

          {/* Light Beam */}
          {isBeamCapable ? (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: beamWidthPx,
                height: beamLengthPx,
                transform: `translate(-50%, calc(-50% - ${height / 2}px)) rotate(${fixture.rotation}deg)`,
                transformOrigin: 'top center',
                pointerEvents: 'none'
              }}
            >
              {/* Main beam with soft edge gradient */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  clipPath: `polygon(50% 0%, ${50 + beamAngle / 2.4}% 100%, ${50 - beamAngle / 2.4}% 100%)`,
                  background: getBeamGradient(),
                  opacity: isSelected ? 1 : 0.9
                }}
              />
              {/* Inner core for harder lights */}
              {(beamStyle === 'hard' || beamStyle === 'spot') && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    width: beamWidthPx * 0.3,
                    height: beamLengthPx * 0.7,
                    transform: 'translateX(-50%)',
                    background: `linear-gradient(180deg, ${alpha(accentColor, isSelected ? 0.8 : 0.6)} 0%, ${alpha(accentColor, 0)} 100%)`,
                    clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
                    opacity: 0.8
                  }}
                />
              )}
              {/* Fixture Head */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -height / 2,
                  left: '50%',
                  transform: `translateX(-50%) rotate(${-fixture.rotation}deg)`,
                  width: width,
                  height: height,
                  borderRadius: borderRadius,
                  bgcolor: backgroundColor,
                  border: `2px solid ${borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isSelected
                    ? `0 0 0 4px ${alpha(accentColor, 0.35)}, 0 8px 24px ${alpha(theme.palette.common.black, 0.35)}`
                    : `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}`,
                  cursor: 'grab',
                  pointerEvents: 'auto',
                  transition: 'all 0.2s ease',
                  '&:active': { cursor: 'grabbing' },
                  '&:hover': {
                    boxShadow: `0 0 0 4px ${alpha(accentColor, 0.45)}, 0 10px 28px ${alpha(theme.palette.common.black, 0.4)}`,
                    transform: `translateX(-50%) rotate(${-fixture.rotation}deg) scale(1.05)`
                  }
                }}
                onMouseDown={(event) => handleFixturePointerDown(event, fixture)}
              >
                <FixtureIcon fontSize="small" sx={{ color: iconColor }} />
                {/* Glow effect for light sources */}
                {(beamStyle === 'hard' || beamStyle === 'spot' || beamStyle === 'warm') && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: -2,
                      borderRadius: borderRadius,
                      background: `radial-gradient(circle, ${alpha(accentColor, 0.4)} 0%, transparent 70%)`,
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </Box>
              {/* Label tooltip */}
              <Chip
                label={fixture.name || fixture.type}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -height - 32,
                  left: '50%',
                  transform: `translateX(-50%) rotate(${-fixture.rotation}deg)`,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24,
                  bgcolor: alpha(theme.palette.background.paper, 0.95),
                  color: theme.palette.text.primary,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                  border: `1px solid ${alpha(accentColor, 0.3)}`,
                  pointerEvents: 'none',
                  opacity: isSelected ? 1 : 0,
                  transition: 'opacity 0.2s ease'
                }}
              />
            </Box>
          ) : null}

          {/* Non-beam fixtures (panels, structures) - NOT camera */}
          {!isBeamCapable && !isCamera ? (
            <Box
              onMouseDown={(event) => handleFixturePointerDown(event, fixture)}
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) rotate(${fixture.rotation}deg)`,
                transformOrigin: 'center',
                width,
                height,
                borderRadius,
                bgcolor: backgroundColor,
                border: '2px solid',
                borderColor,
                color: iconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isSelected
                  ? `0 0 0 3px ${alpha(accentColor, 0.35)}, 0 12px 28px ${alpha(theme.palette.common.black, 0.35)}`
                  : `0 6px 18px ${alpha(theme.palette.common.black, 0.22)}`,
                cursor: 'pointer',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: `0 0 0 3px ${alpha(accentColor, 0.45)}, 0 14px 32px ${alpha(theme.palette.common.black, 0.4)}`,
                  transform: hoverTransform,
                  zIndex: 10
                },
                zIndex: isSelected ? 5 : 1,
                ...(isTalent
                  ? {
                      border: `3px solid ${alpha(accentColor, 0.6)}`,
                      background: `radial-gradient(circle at 30% 30%, ${alpha(theme.palette.common.white, 0.3)} 0%, transparent 50%), ${backgroundColor}`,
                      boxShadow: `0 0 0 4px ${alpha(accentColor, 0.15)}, 0 8px 20px ${alpha(theme.palette.common.black, 0.2)}`
                    }
                  : {}),
                ...(isPanel
                  ? {
                      borderRadius: 4,
                      backdropFilter: 'blur(2px)',
                      backgroundImage: fixture.type === 'Diffusion Panel'
                        ? `repeating-linear-gradient(90deg, transparent, transparent 8px, ${alpha(accentColor, 0.1)} 8px, ${alpha(accentColor, 0.1)} 10px)`
                        : fixture.type === 'Bounce Board'
                          ? `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.95)} 0%, ${alpha('#f5f5f5', 0.9)} 40%, ${alpha(theme.palette.common.white, 0.85)} 50%, ${alpha('#eeeeee', 0.9)} 100%)`
                          : fixture.type === 'Flag / Cutter'
                            ? 'none'
                            : 'none',
                      background: fixture.type === 'Flag / Cutter' ? '#000000' : undefined,
                      border: fixture.type === 'Bounce Board' ? `2px solid ${alpha('#cccccc', 0.6)}` : fixture.type === 'Flag / Cutter' ? '2px solid #000000' : undefined,
                      boxShadow: fixture.type === 'Bounce Board'
                        ? `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}, inset 0 1px 2px ${alpha(theme.palette.common.white, 0.8)}`
                        : fixture.type === 'Flag / Cutter'
                          ? `0 2px 8px ${alpha(theme.palette.common.black, 0.3)}`
                          : undefined,
                      opacity: fixture.type === 'Flag / Cutter' ? 1 : 0.6,
                      '&::before': fixture.type === 'Bounce Board'
                        ? {
                            content: '""',
                            position: 'absolute',
                            inset: 3,
                            border: `1px solid ${alpha('#dddddd', 0.5)}`,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, transparent 0%, ${alpha(theme.palette.common.white, 0.4)} 50%, transparent 100%)`
                          }
                        : undefined,
                      '&::after': fixture.type === 'Bounce Board'
                        ? {
                            content: '""',
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            right: 4,
                            height: '40%',
                            background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.6)} 0%, transparent 100%)`,
                            borderRadius: 1,
                            pointerEvents: 'none'
                          }
                        : undefined
                    }
                  : {}),
                ...(isStructure && fixture.type === 'Window'
                  ? {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 4,
                        border: `1px solid ${alpha(accentColor, 0.4)}`,
                        borderRadius: 2,
                        background: `
                          linear-gradient(90deg, transparent 48%, ${alpha(accentColor, 0.3)} 48%, ${alpha(accentColor, 0.3)} 52%, transparent 52%),
                          linear-gradient(0deg, transparent 48%, ${alpha(accentColor, 0.3)} 48%, ${alpha(accentColor, 0.3)} 52%, transparent 52%)
                        `
                      }
                    }
                  : {}),
                ...(isStructure && fixture.type === 'Door'
                  ? {
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        right: 6,
                        top: '50%',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: alpha(accentColor, 0.5),
                        transform: 'translateY(-50%)'
                      }
                    }
                  : {})
              }}
            >
              <Box sx={{ transform: `rotate(${-fixture.rotation}deg)` }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  {fixture.type !== 'Bounce Board' && fixture.type !== 'Diffusion Panel' && fixture.type !== 'Flag / Cutter' && <FixtureIcon sx={{ color: iconColor, fontSize: '0.7rem' }} />}
                  {fixture.type === 'Talent Marker' && fixture.talentName ? (
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: iconColor }}>
                      {fixture.talentName}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      );
    });
  }, [fixtures, selectedFixtureId, stageDimensions, stageSize.height, stageSize.width, theme, handleFixturePointerDown]);

  return (
    <>
      <GlobalStyles styles={pulseKeyframes} />
      <Stack spacing={3}>
        {/* Header Section */}
        <Paper
          sx={{
            borderRadius: 1,
            p: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: theme.palette.background.paper
          }}
        >
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '-0.02em' }}>
              Lighting Diagram Studio
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip 
              label={`${fixtures.length} Fixtures`} 
              size="medium" 
              sx={{ 
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main
              }} 
            />
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
        {/* Left Side - Saved Diagrams */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: theme.palette.background.paper,
            width: { xs: '100%', lg: 320 },
            minWidth: { lg: 320 },
            flexShrink: 0
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }} flexWrap="wrap" gap={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Saved Diagrams
              </Typography>
              <Chip 
                label={`${savedDiagrams.length} saved`} 
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: theme.palette.success.main
                }}
              />
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={<SaveIcon fontSize="small" />}
              onClick={() => setShowSaveDialog(!showSaveDialog)}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Save
            </Button>
          </Stack>

          {showSaveDialog ? (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 2.5,
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                backgroundColor: theme.palette.background.paper
              }}
            >
              <Stack spacing={2}>
                <TextField
                  label="Diagram Name"
                  placeholder="e.g., Interview Setup"
                  value={saveDiagramName}
                  onChange={(e) => setSaveDiagramName(e.target.value)}
                  fullWidth
                  size="small"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveDiagram();
                    }
                  }}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    onClick={() => {
                      setShowSaveDialog(false);
                      setSaveDiagramName('');
                    }}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveDiagram}
                    disabled={!saveDiagramName.trim()}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5
                    }}
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : null}

          {savedDiagrams.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <FolderOpenIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No saved diagrams yet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Save your setup to load it later.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {savedDiagrams.map((diagram) => {
                const date = new Date(diagram.created);
                const formattedDate = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
                return (
                  <Paper
                    key={diagram.id}
                    elevation={0}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      background: alpha(theme.palette.background.paper, 0.4),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => handleLoadDiagram(diagram)}
                  >
                    <Stack spacing={0.25}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {diagram.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={`${diagram.fixtureCount} fixtures`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formattedDate}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 0.5 }}>
                      <Tooltip title="Load">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLoadDiagram(diagram);
                          }}
                        >
                          <FolderOpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${diagram.name}"?`)) {
                              handleDeleteSavedDiagram(diagram.id);
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* Right Side - Stage Layout */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 420,
            borderRadius: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: theme.palette.background.paper,
            width: { xs: '100%', lg: 'auto' }
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Stage Layout
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag fixtures to design your setup
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={showGrid ? 'Hide grid' : 'Show grid'}>
                <IconButton 
                  onClick={() => setShowGrid((prev) => !prev)} 
                  size="small"
                  sx={{
                    bgcolor: showGrid ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) }
                  }}
                >
                  {showGrid ? <GridOffIcon fontSize="small" /> : <GridOnIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Print PDF">
                <IconButton 
                  onClick={handlePrintDiagram} 
                  size="small"
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.1) } }}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset to default">
                <IconButton 
                  onClick={handleResetDiagram} 
                  size="small"
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.1) } }}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: { xs: 500, sm: 650, md: 850 },
              height: { xs: 500, sm: 650, md: 850 },
              mx: 'auto',
              borderRadius: 1,
              border: '2px solid',
              borderColor: alpha(theme.palette.divider, 0.3),
              overflow: 'hidden',
              userSelect: 'none',
              boxShadow: `inset 0 2px 12px ${alpha(theme.palette.common.black, 0.05)}`,
              ...stageBackgroundStyles
            }}
            id="stage-print"
            ref={stageRef}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setSelectedFixtureId(null);
              }
            }}
          >
            <Box
              data-print-hidden
              className="stage-overlay-border"
              sx={{
                position: 'absolute',
                inset: 0,
                border: '1px dashed',
                borderColor: alpha(theme.palette.text.disabled, 0.4),
                borderRadius: 1,
                pointerEvents: 'none'
              }}
            />
            {renderedFixtures}
          </Box>
        </Paper>
      </Stack>

      {/* Floating Controls Panel */}
      {!controlsPanelOpen && (
        <Fab
          color="primary"
          aria-label="open controls"
          onClick={() => setControlsPanelOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.5)}`
            },
            transition: 'all 0.3s ease'
          }}
        >
          <TuneIcon />
        </Fab>
      )}

      {controlsPanelOpen && (
        <Box
          ref={controlsPanelRef}
          sx={{
            position: 'fixed',
            top: controlsPanelPosition.y,
            left: controlsPanelPosition.x,
            width: { xs: '92vw', sm: 360, md: 420 },
            maxWidth: '92vw',
            maxHeight: { xs: 'calc(100vh - 72px)', sm: 'calc(88vh - 48px)' },
            zIndex: 1300,
            borderRadius: 1,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: theme.palette.background.paper,
            transition: isDraggingControls ? 'none' : 'box-shadow 0.2s ease'
          }}
        >
              <Box
                sx={{
                  px: 2,
                  pt: 2,
                  pb: 1.5,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  maxHeight: { xs: '360px', sm: '340px', md: '320px' },
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}
              >
                <Stack spacing={1.5}
                  sx={{
                    '& .toolbar-section': {
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      alignItems: 'center'
                    }
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ cursor: 'grab', userSelect: 'none' }}
                    onMouseDown={handleControlsPointerDown}
                    onTouchStart={handleControlsPointerDown}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <TuneIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Fixture Controls
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Drag anywhere on this bar to reposition
                        </Typography>
                      </Box>
                    </Stack>
                    <IconButton
                      onClick={() => setControlsPanelOpen(false)}
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.action.hover, 0.6),
                        '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.9) }
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Stack className="toolbar-section" direction="row">
                    {TOOLBAR_FIXTURE_TEMPLATES.map((template) => {
                      const TemplateIcon = template.icon ?? LightbulbIcon;
                      return (
                        <Tooltip key={template.label} title={template.label} placement="bottom">
                          <IconButton
                            size="small"
                            onClick={() => handleAddFixture(template.config)}
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.background.paper, 0.7),
                              color: theme.palette.primary.main
                            }}
                          >
                            <TemplateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      );
                    })}

                    <Tooltip title="Custom Fixture" placement="bottom">
                      <IconButton
                        size="small"
                        onClick={() => handleAddFixture({ name: 'Custom Fixture', type: 'Custom' })}
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1,
                          color: theme.palette.common.white,
                          backgroundColor: theme.palette.primary.main
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {TOOLBAR_STRUCTURE_TEMPLATES.length > 0 && (
                    <Stack className="toolbar-section" direction="row">
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: 'center', fontWeight: 600 }}>
                        Structural:
                      </Typography>
                      {TOOLBAR_STRUCTURE_TEMPLATES.map((template) => {
                        const TemplateIcon = template.icon ?? ViewWeekIcon;
                        return (
                          <Tooltip key={template.label} title={template.label} placement="bottom">
                            <IconButton
                              size="small"
                              onClick={() => handleAddFixture(template.config)}
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1,
                                bgcolor: alpha(theme.palette.secondary.main, 0.12),
                                color: theme.palette.secondary.main
                              }}
                            >
                              <TemplateIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  )}

                  {TALENT_TEMPLATE && (
                    <Stack className="toolbar-section" direction="row">
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: 'center', fontWeight: 600 }}>
                        Talent:
                      </Typography>
                      <Tooltip title="Add Talent Marker" placement="bottom">
                        <IconButton
                          size="small"
                          onClick={() => handleAddFixture(TALENT_TEMPLATE.config)}
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            bgcolor: alpha(theme.palette.info.main, 0.15),
                            color: theme.palette.info.main
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}

                </Stack>
              </Box>

              <Box sx={{ p: 2.5, overflow: 'auto', maxHeight: { xs: 'calc(100vh - 460px)', sm: 'calc(100vh - 440px)', md: 'calc(100vh - 420px)' } }}>
                {selectedFixture ? (
                  <Stack spacing={2.5}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        background: alpha(theme.palette.background.default, 0.4)
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        onClick={() => toggleSection('basic')}
                        sx={{ cursor: 'pointer', mb: expandedSections.basic ? 2 : 0 }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Basic Info
                        </Typography>
                        {expandedSections.basic ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </Stack>

                      <Collapse in={expandedSections.basic}>
                        <Stack spacing={2}>
                          {selectedFixture.type !== 'Talent Marker' && selectedFixture.type !== 'Camera' && (
                            <>
                              <TextField
                                label="Name"
                                value={selectedFixture.name}
                                onChange={(event) => handleUpdateFixture('name', event.target.value)}
                                fullWidth
                                size="small"
                              />

                              <FormControl fullWidth size="small">
                                <InputLabel>Type</InputLabel>
                                <Select
                                  value={selectedFixture.type}
                                  label="Type"
                                  onChange={(event) => handleUpdateFixture('type', event.target.value)}
                                >
                                  {FIXTURE_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                      {type}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </>
                          )}

                          {selectedFixture.type === 'Camera' && (
                            <Stack spacing={2}>
                              <TextField
                                label="Shot Label"
                                value={selectedFixture.shotLabel}
                                onChange={(event) => handleUpdateFixture('shotLabel', event.target.value)}
                                fullWidth
                                size="small"
                              />
                              <FormControl fullWidth size="small">
                                <InputLabel>Lens</InputLabel>
                                <Select
                                  value={selectedFixture.focalLength?.toString() || '35'}
                                  label="Lens"
                                  onChange={(event) => {
                                    const focalLength = parseInt(event.target.value, 10);
                                    handleUpdateFixture('focalLength', focalLength);
                                    // Auto-set lens name based on focal length
                                    const lensNames = {
                                      16: '16mm Wide',
                                      24: '24mm Wide',
                                      35: '35mm Standard',
                                      50: '50mm Normal',
                                      85: '85mm Portrait',
                                      135: '135mm Telephoto'
                                    };
                                    handleUpdateFixture('lens', lensNames[focalLength] || `${focalLength}mm Lens`);
                                  }}
                                >
                                  <MenuItem value="16">16mm f/2.8 (Ultra Wide)</MenuItem>
                                  <MenuItem value="24">24mm f/1.4 (Wide)</MenuItem>
                                  <MenuItem value="35">35mm f/1.8 (Standard)</MenuItem>
                                  <MenuItem value="50">50mm f/1.8 (Normal)</MenuItem>
                                  <MenuItem value="85">85mm f/1.8 (Portrait)</MenuItem>
                                  <MenuItem value="135">135mm f/2.0 (Telephoto)</MenuItem>
                                </Select>
                              </FormControl>
                              <TextField
                                label="Custom Focal Length (mm)"
                                type="number"
                                value={selectedFixture.focalLength ?? ''}
                                onChange={(event) => handleUpdateFixture('focalLength', event.target.value)}
                                fullWidth
                                size="small"
                                placeholder="e.g. 28, 40, 105..."
                              />
                              <TextField
                                label="Camera Notes"
                                value={selectedFixture.cameraNotes}
                                onChange={(event) => handleUpdateFixture('cameraNotes', event.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                                size="small"
                                placeholder="Blocking, filtration, movement, operator notes..."
                              />
                            </Stack>
                          )}

                          {selectedFixture.type === 'Talent Marker' && (
                            <Stack spacing={2}>
                              <TextField
                                label="Talent Name"
                                value={selectedFixture.talentName}
                                onChange={(event) => handleUpdateFixture('talentName', event.target.value)}
                                fullWidth
                                size="small"
                              />
                              <TextField
                                label="Movement / Blocking Notes"
                                value={selectedFixture.movementNotes}
                                onChange={(event) => handleUpdateFixture('movementNotes', event.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                                size="small"
                                placeholder="Marks, walk direction, timing cues..."
                              />
                            </Stack>
                          )}

                        </Stack>
                      </Collapse>
                    </Paper>

                    {STRUCTURE_TYPES.includes(selectedFixture.type) && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: alpha(theme.palette.background.default, 0.4)
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Length</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {selectedFixture.length?.toFixed(2)}
                            </Typography>
                          </Stack>
                          <Slider
                            min={getStructureMinLength(stageDimensions.unit)}
                            max={Math.max(getStructureMinLength(stageDimensions.unit), stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width)}
                            step={stageDimensions.unit === 'ft' ? 1 / 12 : 0.01}
                            value={Math.min(
                              Math.max(getStructureMinLength(stageDimensions.unit), stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width),
                              Math.max(getStructureMinLength(stageDimensions.unit), Number.isFinite(Number(selectedFixture.length)) ? Number(selectedFixture.length) : getStructureMinLength(stageDimensions.unit))
                            )}
                            onChange={(_, value) => {
                              const nextValue = Array.isArray(value) ? value[0] : value;
                              handleUpdateFixture('length', nextValue);
                              setStructureDimensionInputs((prev) => ({
                                ...prev,
                                [selectedFixture.id]: {
                                  ...(prev[selectedFixture.id] ?? {}),
                                  length: formatStructureInputValue(nextValue, stageDimensions.unit)
                                }
                              }));
                            }}
                          />

                          <TextField
                            label="Structure Color"
                            type="color"
                            value={selectedFixture.color}
                            onChange={(event) => handleUpdateFixture('color', event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              width: 150,
                              '& input': {
                                padding: 0,
                                height: 40
                              }
                            }}
                          />
                        </Stack>
                      </Paper>
                    )}

                    {!['Camera', 'Talent Marker', ...STRUCTURE_TYPES].includes(selectedFixture.type) && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: alpha(theme.palette.background.default, 0.4)
                        }}
                      >
                        <Stack spacing={1.5}>
                          {selectedFixture.type === 'Bounce Board' || selectedFixture.type === 'Diffusion Panel' || selectedFixture.type === 'Flag / Cutter' ? (
                            <>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2">Length</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {selectedFixture.length?.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Slider
                                min={getStructureMinLength(stageDimensions.unit)}
                                max={Math.max(getStructureMinLength(stageDimensions.unit), stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width)}
                                step={stageDimensions.unit === 'ft' ? 1 / 12 : 0.01}
                                value={Math.min(
                                  Math.max(getStructureMinLength(stageDimensions.unit), stageDimensions.width || DEFAULT_STAGE_DIMENSIONS.width),
                                  Math.max(getStructureMinLength(stageDimensions.unit), Number.isFinite(Number(selectedFixture.length)) ? Number(selectedFixture.length) : getStructureMinLength(stageDimensions.unit))
                                )}
                                onChange={(_, value) => {
                                  const nextValue = Array.isArray(value) ? value[0] : value;
                                  handleUpdateFixture('length', nextValue);
                                  setStructureDimensionInputs((prev) => ({
                                    ...prev,
                                    [selectedFixture.id]: {
                                      ...(prev[selectedFixture.id] ?? {}),
                                      length: formatStructureInputValue(nextValue, stageDimensions.unit)
                                    }
                                  }));
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2">Beam Angle</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(selectedFixture.beamAngle)}°
                                </Typography>
                              </Stack>
                              <Slider
                                min={10}
                                max={120}
                                value={selectedFixture.beamAngle}
                                onChange={(_, value) => handleUpdateFixture('beamAngle', value)}
                              />
                            </>
                          )}
                          <TextField
                            label="Fixture Color"
                            type="color"
                            value={selectedFixture.color}
                            onChange={(event) => handleUpdateFixture('color', event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              width: 150,
                              '& input': {
                                padding: 0,
                                height: 40
                              }
                            }}
                          />
                        </Stack>
                      </Paper>
                    )}

                    {selectedFixture.type !== 'Talent Marker' && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        background: alpha(theme.palette.background.default, 0.4)
                      }}
                    >
                      <Stack spacing={1.5}>
                        {selectedFixture.type !== 'Camera' && !['Key Light', 'Fill Light', 'Back Light', 'Practical', 'Spot', 'Ambient', 'Custom', 'Bounce Board', 'Diffusion Panel', 'Flag / Cutter'].includes(selectedFixture.type) && (
                          <>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                              <Typography variant="body2">Horizontal Position</Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(selectedFixture.x)}%
                                </Typography>
                                <TextField
                                  value={fixturePositionInputs.x}
                                  onChange={(event) => handleFixturePositionInputChange('x', event.target.value)}
                                  onBlur={() => handleFixturePositionInputBlur('x')}
                                  onKeyDown={(event) => handleFixturePositionInputKeyDown(event, 'x')}
                                  placeholder="e.g. 42.5"
                                  size="small"
                                  sx={{ width: 88 }}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    inputProps: { inputMode: 'decimal', pattern: '[0-9]*' }
                                  }}
                                />
                              </Stack>
                            </Stack>
                            <Slider
                              min={1}
                              max={95}
                              step={1}
                              value={selectedFixture.x}
                              onChange={(_, value) => handleUpdateFixture('x', value)}
                            />

                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                              <Typography variant="body2">Vertical Position</Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(selectedFixture.y)}%
                                </Typography>
                                <TextField
                                  value={fixturePositionInputs.y}
                                  onChange={(event) => handleFixturePositionInputChange('y', event.target.value)}
                                  onBlur={() => handleFixturePositionInputBlur('y')}
                                  onKeyDown={(event) => handleFixturePositionInputKeyDown(event, 'y')}
                                  placeholder="e.g. 58.0"
                                  size="small"
                                  sx={{ width: 88 }}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    inputProps: { inputMode: 'decimal', pattern: '[0-9]*' }
                                  }}
                                />
                              </Stack>
                            </Stack>
                            <Slider
                              min={1}
                              max={95}
                              step={1}
                              value={selectedFixture.y}
                              onChange={(_, value) => handleUpdateFixture('y', value)}
                            />
                          </>
                        )}

                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Tilt / Rotation</Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(selectedFixture.rotation)}°
                            </Typography>
                            <TextField
                              value={fixturePositionInputs.rotation}
                              onChange={(event) => handleFixturePositionInputChange('rotation', event.target.value)}
                              onBlur={() => handleFixturePositionInputBlur('rotation')}
                              onKeyDown={(event) => handleFixturePositionInputKeyDown(event, 'rotation')}
                              placeholder="e.g. 45"
                              size="small"
                              sx={{ width: 88 }}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                inputProps: { inputMode: 'decimal', pattern: '[0-9]*' }
                              }}
                            />
                          </Stack>
                        </Stack>
                        <Slider
                          min={-180}
                          max={180}
                          step={1}
                          value={selectedFixture.rotation}
                          onChange={(_, value) => handleUpdateFixture('rotation', value)}
                        />
                      </Stack>
                    </Paper>
                    )}

                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        background: alpha(theme.palette.background.default, 0.4)
                      }}
                    >
                      <Stack spacing={1.5}>
                        <TextField
                          label="Notes"
                          value={selectedFixture.notes}
                          onChange={(event) => handleUpdateFixture('notes', event.target.value)}
                          multiline
                          minRows={3}
                          fullWidth
                          placeholder="Intensity ratios, gel references, rigging notes..."
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            startIcon={<ContentCopyIcon fontSize="small" />}
                            onClick={() => handleDuplicateFixture(selectedFixture.id)}
                          >
                            Duplicate
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon fontSize="small" />}
                            onClick={() => handleDeleteFixture(selectedFixture.id)}
                          >
                            Remove
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select a fixture on the diagram to edit its properties, or use the quick add shortcuts to place new lights.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<LightbulbIcon fontSize="small" />}
                      onClick={() => handleAddFixture({ name: 'Key Light', type: 'Key Light', intensity: 80 })}
                    >
                      Add Key Light
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Stack>
      <Box sx={{ height: 100 }} />
    </>
  );
};

export default LightingDiagramTool;
